import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { AiOutline } from '@ready/shared';
import { useApi, type ContentOwner, type Course } from '@/lib/api';
import { Button, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';
import {
  AiPanel,
  DraftingNote,
  SourceFields,
  emptySource,
  sourceInput,
  useAiDraft,
  type SourceValue,
} from './ai-assist';

/**
 * Drafts modules and lessons for a course from a brief (and optional source
 * material). The author edits the titles, removes what they don't want, then
 * adds the rest to the course as empty lessons to write.
 */
export function OutlineAssist({
  course,
  owner,
  onClose,
}: {
  course: Course;
  owner: ContentOwner;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [brief, setBrief] = useState(course.summary ?? '');
  const [modules, setModules] = useState(4);
  const [lessons, setLessons] = useState(3);
  const [source, setSource] = useState<SourceValue>(emptySource);
  const ai = useAiDraft('course_outline');
  const [outline, setOutline] = useState<AiOutline | null>(null);
  const [useDetails, setUseDetails] = useState(!course.description);

  const shown = outline ?? ai.draft?.outline ?? null;
  const edit = (next: AiOutline) => setOutline(next);
  const apply = useMutation({
    mutationFn: (o: AiOutline) =>
      api.appendOutline(course.id, {
        modules: o.modules,
        ...(useDetails
          ? { details: { summary: o.summary, description: o.description, outcomes: o.outcomes } }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] });
      onClose();
    },
  });

  const lessonCount = shown?.modules.reduce((n, m) => n + m.lessons.length, 0) ?? 0;

  return (
    <AiPanel title="Draft an outline with AI" onClose={onClose}>
      {!shown && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setOutline(null);
            ai.start({
              kind: 'course_outline',
              courseId: course.id,
              brief,
              modules,
              lessonsPerModule: lessons,
              ...sourceInput(source),
            });
          }}
        >
          <Field
            label="What is the course about?"
            htmlFor="ai-brief"
            hint="Topic, who it's for, what they should be able to do afterwards."
          >
            <Textarea
              id="ai-brief"
              rows={4}
              required
              minLength={10}
              maxLength={4000}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Modules" htmlFor="ai-modules">
              <Select
                id="ai-modules"
                value={modules}
                onChange={(e) => setModules(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
            <Field label="Lessons per module" htmlFor="ai-lessons">
              <Select
                id="ai-lessons"
                value={lessons}
                onChange={(e) => setLessons(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
          </div>
          <SourceFields id="ai-outline" owner={owner} value={source} onChange={setSource} />
          <Button type="submit" disabled={ai.drafting}>
            {ai.drafting ? 'Drafting…' : 'Draft outline'}
          </Button>
          <DraftingNote active={ai.drafting} />
          <ErrorText error={ai.error} />
        </form>
      )}

      {shown && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Edit titles and remove anything you don't want. Lessons are added empty; open each one
            to write it (AI assist can draft those too).
          </p>
          <ol className="space-y-3">
            {shown.modules.map((m, mi) => (
              <li key={mi} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Module ${mi + 1} title`}
                    className="min-w-0 flex-1 font-medium"
                    value={m.title}
                    onChange={(e) =>
                      edit({
                        ...shown,
                        modules: shown.modules.map((x, i) =>
                          i === mi ? { ...x, title: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <RemoveButton
                    label={`Remove module ${m.title}`}
                    onClick={() =>
                      edit({ ...shown, modules: shown.modules.filter((_, i) => i !== mi) })
                    }
                  />
                </div>
                <ul className="mt-2 space-y-2 border-l border-border pl-3">
                  {m.lessons.map((l, li) => (
                    <li key={li} className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <Input
                          aria-label={`Lesson ${li + 1} title`}
                          value={l.title}
                          onChange={(e) =>
                            edit({
                              ...shown,
                              modules: shown.modules.map((x, i) =>
                                i === mi
                                  ? {
                                      ...x,
                                      lessons: x.lessons.map((y, j) =>
                                        j === li ? { ...y, title: e.target.value } : y,
                                      ),
                                    }
                                  : x,
                              ),
                            })
                          }
                        />
                        {l.summary && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {l.summary}
                            {l.estimatedMinutes ? ` · ${l.estimatedMinutes} min` : ''}
                          </p>
                        )}
                      </div>
                      <RemoveButton
                        label={`Remove lesson ${l.title}`}
                        disabled={m.lessons.length === 1}
                        onClick={() =>
                          edit({
                            ...shown,
                            modules: shown.modules.map((x, i) =>
                              i === mi
                                ? { ...x, lessons: x.lessons.filter((_, j) => j !== li) }
                                : x,
                            ),
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-primary"
              checked={useDetails}
              onChange={(e) => setUseDetails(e.target.checked)}
            />
            <span>
              Also use the drafted summary, description and {shown.outcomes.length} learning
              outcomes
              <span className="block text-xs text-muted-foreground">{shown.summary}</span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={apply.isPending || shown.modules.length === 0}
              onClick={() => apply.mutate(shown)}
            >
              <Plus className="size-4" aria-hidden />
              Add {shown.modules.length} module{shown.modules.length === 1 ? '' : 's'} and{' '}
              {lessonCount} lesson{lessonCount === 1 ? '' : 's'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setOutline(null);
                ai.reset();
              }}
            >
              Start over
            </Button>
          </div>
          <ErrorText error={apply.error} />
        </div>
      )}
    </AiPanel>
  );
}

function RemoveButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
    >
      <Trash2 className="size-4" />
    </button>
  );
}
